import { useState } from 'react';
import { UseFormRegisterReturn } from 'react-hook-form';
import { PasswordToggleButton } from './PasswordToggleButton';
import styles from './LabeledFormField.module.css';

interface LabeledFormFieldProps {
  label: string;
  icon: string;
  registration: UseFormRegisterReturn;
  type?: 'text' | 'email' | 'password';
  placeholder?: string;
  required?: boolean;
  error?: string;
  autoFocus?: boolean;
  // Applied alongside the default field-group styling, e.g. so a consumer
  // that wants divider borders between fields (ChangePassword) can add that
  // without every other consumer inheriting it.
  className?: string;
}

// A labeled input with a leading icon and an error message slot, used across
// the Forgot/Reset/Change password screens - previously this label + icon +
// input-wrap + error-message block (plus a show/hide toggle for password
// fields) was duplicated 7 times across those 3 files.
export const LabeledFormField = ({
  label,
  icon,
  registration,
  type = 'text',
  placeholder,
  required = false,
  error,
  autoFocus = false,
  className,
}: LabeledFormFieldProps) => {
  const [showValue, setShowValue] = useState(false);
  const isPassword = type === 'password';
  let inputType = type;
  if (isPassword) {
    inputType = showValue ? 'text' : 'password';
  }

  return (
    <div className={`${styles.fieldGroup} ${className || ''}`}>
      <label className={styles.label}>
        {label} {required && <span className={styles.required}>*</span>}
      </label>
      <div className={`${styles.inputWrap} ${error ? styles.inputWrapError : ''}`}>
        <span className={`material-icons ${styles.leadingIcon}`}>{icon}</span>
        <input
          className={styles.input}
          type={inputType}
          placeholder={placeholder}
          autoFocus={autoFocus}
          {...registration}
        />
        {isPassword && (
          <PasswordToggleButton
            visible={showValue}
            onToggle={() => setShowValue((prev) => !prev)}
            className={styles.eyeToggle}
            iconClassName={styles.eyeIcon}
            tabIndex={-1}
          />
        )}
      </div>
      {error && <div className={styles.errorMsg}>{error}</div>}
    </div>
  );
};
